import React, { memo, useCallback, useEffect, useState } from 'react';
import { checkBoxEnabledStyle, style } from 'components/PivotGrid/defaultStyle';
import { Box, Stack } from '@mui/material';
import { styled } from '@mui/material/styles';
import {
    ColDef,
    FilterChangedEvent,
    GridApi,
    GridOptions,
    GridReadyEvent,
    ITooltipParams,
    SideBarDef,
} from 'ag-grid-community';
import { BudgetProposalT } from 'routes/BudgetModule/config/types';
import Loading from 'components/Loading';
import { hideTCDColumnByYear } from 'ui/AgGridReact/helpers';
import { trackEventBudget } from 'helpers/trackerHelpers';
import { HttpError } from 'store/APIClient';
import { getValueFormatter } from 'ui/AgGridReact/formatters';
import AgGridReactExposingApi from 'routes/BudgetModule/components/shared/AgGridReactExposingAPI';
import { ExcelExportConfig } from 'routes/BudgetModule/contexts/GridApiContext';
import ProjectionFilter from 'routes/BudgetModule/components/BudgetOverview/AllProjections/Components/ProjectionFilter';
import {
    aggFuncs,
    getColDefsForColumnsPivotedByYear,
} from '../helpers/colDefinitions';
import AtterrissageProjectionDetailCellRenderer from './AtterrissageProjectionDetailCellRenderer';
import ProjectionStatusSidePanelCellRenderer from './ProjectionStatusSidePanelCellRenderer';
import {
    AbstractProjectionDetailsT,
    ProjectionFiltersParams,
    ProjectionVentilation,
} from '../helpers/types';
import HttpErrorWrapper from '../../ExpenseIncome/components/HttpErrorWrapper';
import PluriannualProjectionDetailCellRenderer from './PluriannualProjectionDetailCellRenderer';
import { useFlexibleRowGroupings } from '../hooks/gridHooks';
import { handleSortChanged, setInitialSort } from '../helpers/gridHelpers';
import { useFullScreen } from 'contexts/FullScreenContext';
import { useGridFiltersHelper } from '../../Analyse/hooks/useGridFiltersHelper';
import {
    BUDGET_EXCEL_STYLES,
    BUDGET_EXCEL_EXPORT_PARAMS,
    getBudgetExcelStringCellClass,
} from 'routes/BudgetModule/config/excelExportHelpers';

const PROJECTION_DEFAULT_COL_DEF = {
    flex: 1,
    filter: false,
    minWidth: 100,
    sortable: true,
    resizable: true,
    suppressMovable: true,
    lockPosition: 'right' as const,
    lockPinned: true,
    suppressFiltersToolPanel: true,
};

const PROJECTION_AUTO_GROUP_COLUMN_DEF: ColDef = {
    cellRendererParams: {
        suppressCount: true,
    },
    suppressMovable: false,
    lockPosition: false,
    pinned: 'left' as const,
    width: 135,
    cellClass: getBudgetExcelStringCellClass,
    useValueFormatterForExport: false,
    tooltipValueGetter: (params: ITooltipParams) => {
        const valueFormatter = getValueFormatter(params);
        if (!valueFormatter) return params.value ?? '';
        return valueFormatter(params);
    },
};

const SIDE_BAR: SideBarDef = {
    toolPanels: [
        {
            id: 'columns',
            labelKey: 'columns',
            labelDefault: 'sideBar',
            iconKey: 'columns',
            toolPanel: 'agColumnsToolPanel',
            toolPanelParams: {
                // prevent user from disabling the pivot mode
                suppressPivotMode: true,
                // prevent user from removing Exercice from the pivoted columns
                suppressPivots: true,
                // prevent user from removing ventilation cols
                suppressRowGroups: true,
            },
        },
        {
            id: 'filters',
            labelDefault: 'Filters',
            labelKey: 'filters',
            iconKey: 'filter',
            toolPanel: 'agFiltersToolPanel',
            toolPanelParams: {
                suppressFilterSearch: true,
                suppressExpandAll: true,
                suppressSyncLayoutWithGrid: true,
            },
        },
    ],
};

const StyProjectionGrid = styled(Box, {
    name: 'StyProjectionGrid',
    shouldForwardProp: (prop) => prop !== 'isDaf',
})<{ isDaf?: boolean }>(({ isDaf = false, theme }) => ({
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    '& .mui-pivot-grid-wrapper': {
        ...style,
        ...checkBoxEnabledStyle,
        flex: 1,
        overflow: 'hidden',
        // add a left border on the whole table on Resource view
        ...(!isDaf
            ? {
                  '& .ag-root-wrapper': {
                      borderLeft: '1px solid #e2e2e2',
                  },
              }
            : {}),
        // hide vertical separator in the middle of the scroll bar
        '& .ag-body-horizontal-scroll .ag-horizontal-right-spacer': {
            borderLeft: 'none',
        },
        '& .ag-pinned-left-header': {
            borderBottom: '1px solid #e2e2e2',
        },
        '& .ag-root-wrapper-body': {
            height: 'max-content',
            '--ag-row-group-indent-size': '0.25em',
        },
        // target only gestionnaire/service group header rows
        '& .ag-row-group.ag-row-level-0': {
            fontWeight: '500',
            // create a heavier separation between each service/gestionnaire on Daf view
            ...(isDaf
                ? {
                      '&:not(:first-of-type)': {
                          borderTop: '2px solid #efefef',
                      },
                  }
                : {}),
        },
        '& .ag-pinned-right-header': {
            borderLeft: 'none',
            background: '#f8f8f8',
            borderTop: '1px solid #e2e2e2',
            borderBottom: '1px solid #e2e2e2',
            '& .ag-header-group-text': {
                opacity: 0,
            },
            '& .ag-header-group-cell': {
                backgroundColor: theme.palette.lightGrey,
                borderRadius: '15px 0 0 0',
                borderTop: 'none',
            },
            '& .ag-header-cell': {
                backgroundColor: theme.palette.lightGrey,
            },
            '& .ag-header-row': {
                borderBottom: '1px solid #e2e2e2',
                '&:last-of-type > .ag-header-cell': {
                    borderTop: 'none',
                },
            },
        },
        '& .ag-cell > span': {
            width: '100%',
        },
        '& .ag-pinned-right-cols-container': {
            '& .ag-cell:last-of-type': {
                borderRightColor: '#e2e2e2!important',
            },
        },
        // create a rounded corner on the bottom left of the pivot grid, without weird borders
        '& .ag-pinned-right-cols-container > .ag-row-footer': {
            borderBottom: '1px solid #e2e2e2',
            '& > .ag-cell:first-of-type': {
                borderRadius: '0 0 0 15px',
                borderBottom: 'none',
            },
        },
        '& .MuiOutlinedInput-root': {
            background: 'white',
        },
    },
    '& .ag-theme-material': {
        width: '100%',
        fontFamily: 'Roboto',
        height: '100%',
        '--ag-value-change-value-highlight-background-color':
            theme.palette.primary.background,
        '--ag-value-change-delta-down-color': theme.palette.green,
        '--ag-value-change-delta-up-color': theme.palette.red,
    },
    '& .ag-overlay': {
        display: 'none',
    },
    '& .ag-row-footer': {
        textTransform: 'uppercase',
        fontWeight: 'bold',
    },
}));

type WrapperProps = {
    isLoading: boolean;
    error: HttpError | null;
    isDaf: boolean;
    children: React.ReactNode;
};

export const ProjectionGridWrapper = ({
    isLoading,
    error,
    isDaf,
    children,
}: WrapperProps) => {
    if (isLoading) {
        return (
            <StyProjectionGrid>
                <Loading />
            </StyProjectionGrid>
        );
    }
    return (
        <HttpErrorWrapper isError={error !== null} error={error}>
            <StyProjectionGrid isDaf={isDaf}>{children}</StyProjectionGrid>
        </HttpErrorWrapper>
    );
};

type Props = {
    budgetProposal: BudgetProposalT;
    colDefs: ColDef[];
    filters: ProjectionFiltersParams;
    projectionsDetails: AbstractProjectionDetailsT[];
    unicityDimensions: ProjectionVentilation.SoftwareIds[];
    isDaf?: boolean;
    trackEvents: {
        onRowGroupOpenedTrackEvent?: string;
        onGroupChangedTrackEvent: string;
        onFilteredTrackEvent?: string;
    };
    excelExport?: ExcelExportConfig;
    shouldShowOpenedGroups: boolean;
};

const ProjectionGrid = ({
    budgetProposal,
    colDefs,
    filters,
    projectionsDetails,
    unicityDimensions,
    isDaf = false,
    trackEvents,
    excelExport = undefined,
    shouldShowOpenedGroups,
}: Props): React.ReactElement => {
    const { exercice } = budgetProposal;
    const [gridApi, setGridApi] = useState<GridApi | null>(null);
    const [gridOptions] = useState<GridOptions>({
        onRowGroupOpened: () => {
            if (isDaf && trackEvents.onRowGroupOpenedTrackEvent) {
                trackEventBudget(trackEvents.onRowGroupOpenedTrackEvent);
            }
        },
        defaultExcelExportParams: BUDGET_EXCEL_EXPORT_PARAMS,
        excelStyles: BUDGET_EXCEL_STYLES,
    });

    const { gridFilters, updateProjectionGridFilters: updateGridFilters } =
        useGridFiltersHelper(exercice);

    const onGridReady = useCallback((params: GridReadyEvent) => {
        setGridApi(params.api);
        setInitialSort(params.api, exercice);
    }, []);

    useEffect(() => {
        if (gridApi !== null && gridFilters !== undefined) {
            gridApi.setFilterModel(gridFilters.projectionsGridFilters);
        }
    }, [JSON.stringify(gridFilters), gridApi]);

    const onFiltersChanged = (event: FilterChangedEvent) => {
        if (
            updateGridFilters !== undefined &&
            event.source === 'columnFilter'
        ) {
            updateGridFilters(event);
        }
    };

    // This state is used to keep track of the grouping columns order
    // If the user changes the order of the grouping columns, this is
    // updates, which updates the coldefs
    const { updatedColDefs, onColumnMoved } = useFlexibleRowGroupings(
        colDefs,
        trackEvents.onGroupChangedTrackEvent,
    );

    const processPivotResultColDef = useCallback(
        (colDef: ColDef) =>
            hideTCDColumnByYear(
                colDef,
                exercice,
                getColDefsForColumnsPivotedByYear,
            ),
        [exercice],
    );

    const getRowId = useCallback(
        (params: { data: AbstractProjectionDetailsT }) =>
            unicityDimensions.map((dim) => params.data[dim] ?? '').join('_'),
        [unicityDimensions],
    );

    const { isFullScreen } = useFullScreen();

    return (
        <>
            {isDaf && (
                <Stack direction="row" sx={{ ml: '16px', my: 1.5 }} spacing={2}>
                    {filters.map((f) => (
                        <ProjectionFilter
                            data={f.data}
                            filtersState={f.filtersState}
                            setFiltersState={f.setFiltersState}
                            labelField={f.labelField}
                            title={f.title}
                            key={f.labelField}
                            onFilteredTrackEvent={
                                trackEvents.onFilteredTrackEvent
                            }
                        />
                    ))}
                </Stack>
            )}

            <div className="mui-pivot-grid-wrapper">
                <AgGridReactExposingApi
                    pagination={!isFullScreen}
                    paginationPageSize={10}
                    className="ag-theme-material"
                    defaultColDef={PROJECTION_DEFAULT_COL_DEF}
                    rowHeight={36}
                    groupDisplayType="multipleColumns"
                    sideBar={SIDE_BAR}
                    onColumnMoved={onColumnMoved}
                    onSortChanged={({ api }) =>
                        handleSortChanged(api, exercice)
                    }
                    onFilterChanged={onFiltersChanged}
                    onGridReady={onGridReady}
                    headerHeight={36}
                    enableBrowserTooltips
                    // For the rowNodeId, we need the projection_details.id but only rows of the current exercice have a projection_details.
                    // For this reason, if it's not provided we create an id from the chapter and code of the row
                    // Note: We will probably have to change this when the projection will be grouped by something else than chapter
                    getRowId={
                        getRowId // needed for transactions
                    }
                    rowData={projectionsDetails}
                    components={{
                        atterrissageProjectionDetailCellRenderer:
                            AtterrissageProjectionDetailCellRenderer,
                        pluriannualProjectionDetailCellRenderer:
                            PluriannualProjectionDetailCellRenderer,
                        projectionStatusSidepanelCellRenderer:
                            ProjectionStatusSidePanelCellRenderer,
                    }}
                    gridOptions={gridOptions}
                    pivotMode
                    tooltipShowDelay={0}
                    autoGroupColumnDef={{
                        ...PROJECTION_AUTO_GROUP_COLUMN_DEF,
                    }}
                    grandTotalRow="bottom"
                    columnDefs={updatedColDefs}
                    aggFuncs={aggFuncs}
                    processPivotResultColDef={processPivotResultColDef}
                    excelExport={excelExport}
                    showOpenedGroup={shouldShowOpenedGroups}
                />
            </div>
        </>
    );
};

export default memo(ProjectionGrid);
